import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import type { SyncRun } from '@/lib/api/sources.ts'
import { isRunActive } from './useSyncRun.ts'

const DISMISSED_PREFIX = 'ludus.syncNotice.'

/** Never for a cron run: a modal every five minutes is worse than a stale number. */
function warnsAbout(run: SyncRun | null): boolean {
  return run !== null && isRunActive(run) && (run.trigger === 'connect' || run.trigger === 'manual')
}

function wasDismissed(runId: string): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_PREFIX + runId) !== null
  } catch {
    return false
  }
}

function rememberDismissal(runId: string): void {
  try {
    sessionStorage.setItem(DISMISSED_PREFIX + runId, '1')
  } catch {
    // A browser that refuses storage costs one repeated notice, not a broken board.
  }
}

/** `flushSync` is required: the API tweens against the DOM as the callback leaves it. */
function morph(update: () => void): void {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  if (reduced || typeof document.startViewTransition !== 'function') {
    update()
    return
  }
  document.startViewTransition(() => flushSync(update))
}

export function useSyncNotice(run: SyncRun | null) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (run && warnsAbout(run) && !wasDismissed(run.id)) setOpen(true)
  }, [run])

  return {
    open,
    dismiss() {
      if (run) rememberDismissal(run.id)
      // Only a running sync has a widget to fly into.
      if (isRunActive(run)) morph(() => setOpen(false))
      else setOpen(false)
    },
  }
}
